import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setRegion } from "../../redux/actions";
import { AppState } from "../../redux/types";
import { DEFAULT_REGION, getRegionConfig, RegionCode } from "../../utils/constants";
import { fetchIPCountry, mapCountryToRegion } from "../../utils/api-helper/ipinfo";
import { buildTranslatedPath, getLocaleFromPath } from "../config";

function mapLocaleToRegion(locale: string): RegionCode | null {
    const value = locale.toLowerCase();
    if (value.includes("-fi")) return "FI";
    if (value.includes("-se")) return "SE";
    if (value.includes("-ie")) return "IE";
    if (value.includes("-gb") || value.includes("-uk")) return "UK";
    if (value.startsWith("fi")) return "FI";
    if (value.startsWith("sv")) return "SE";
    if (value.startsWith("en")) return "EN-GLOBAL";
    return null;
}

/**
 * On first app load (no region in localStorage):
 * Bypasses for logged-in users (their preferred_locale profile takes strict precedence).
 * Detects visitor region via IPinfo Lite (falling back to browser languages, then FI).
 * Automatically redirects the guest user to the correct localized language path and 
 * sets their active region in localStorage, ensuring they land immediately on the correct site.
 */
const useRegionDetector = () => {
    const dispatch = useDispatch();
    const currentRegion = useSelector((state: AppState) => state.region.code);
    const isLoggedIn = useSelector((state: AppState) => state.user.isLoggedIn);

    useEffect(() => {
        // Logged-in users use their profile's preferred_locale (applied during login/profile load).
        // Skip automatic detection/redirects entirely.
        if (isLoggedIn) return;

        // If the user has already chosen a region or visited the site before, honor their choice.
        const hasExplicitRegion = localStorage.getItem("regionBannerDismissed") === "true";
        if (hasExplicitRegion) return;

        let isMounted = true;

        const runDetection = async () => {
            const country = await fetchIPCountry();
            if (!isMounted) return;

            let detected: RegionCode = mapCountryToRegion(country) ?? "FI";

            if (country === null) {
                // Fallback to browser mapping if IPinfo fetch fails
                const locales = navigator.languages && navigator.languages.length > 0
                    ? navigator.languages
                    : [navigator.language];
                detected = locales.map(mapLocaleToRegion).find((region): region is RegionCode => !!region) ?? DEFAULT_REGION;
            }

            // Only redirect/reload if the detected region is different from the current bootstrapped region
            if (detected !== currentRegion) {
                const currentLang = getLocaleFromPath(window.location.pathname);
                const regionConfig = getRegionConfig(detected);

                localStorage.setItem("region", detected);
                localStorage.setItem("language", regionConfig.lang);
                localStorage.setItem("regionBannerDismissed", "true");

                dispatch(setRegion(detected));

                const newPath = buildTranslatedPath(window.location.pathname, regionConfig.lang, currentLang) + window.location.search;
                
                // If language changes, reload path via window.location.replace to trigger server-side Weglot proxy
                if (regionConfig.lang !== currentLang) {
                    window.location.replace(newPath);
                } else {
                    // Same language but different regional pricing catalog (e.g. FI EUR to IE EUR)
                    // Reload the page to ensure all bootstrappers align
                    window.location.reload();
                }
            } else {
                // If the detected region matches the current region, save it to stop further lookups
                localStorage.setItem("region", detected);
                const regionConfig = getRegionConfig(detected);
                localStorage.setItem("language", regionConfig.lang);
                localStorage.setItem("regionBannerDismissed", "true");
            }
        };

        runDetection();

        return () => {
            isMounted = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, currentRegion, dispatch]);

    return currentRegion;
};

export default useRegionDetector;

